import { createStore } from '@/shared/stores/config';

import type { ICheckpointFact, TCheckpointDecision } from './astral2-review';

/**
 * State for the checkpoint approval dialog opened from a `request_review`
 * (verify_checkpoint) chat card that carries `checkpoint_facts`. Unlike the IDP
 * panel (document/field editing), this is a read-only review of runtime values
 * followed by a single approve/reject decision, so it gets its own lightweight
 * store + dialog rather than being squeezed through the IDP renderer.
 *
 * Bare approve/reject checkpoints (no facts) never touch this store — they're
 * rendered inline in the chat with their own buttons.
 */
interface ICheckpointReviewState {
  /** Whether the approval dialog is currently open. */
  isOpen: boolean;
  /** The agent's explanatory message shown above the facts. */
  message?: string;
  /** The runtime values the user reviews before deciding. */
  facts: ICheckpointFact[];
  /**
   * Resolves the underlying `request_review` tool with the chosen decision and
   * the reviewed values keyed by field label (empty on reject, since an
   * unapproved edit is not a correction). Returns true on success so the dialog
   * can close itself (mirrors the IDP save's boolean contract).
   */
  onDecide?: (
    decision: TCheckpointDecision,
    values?: Record<string, string>,
  ) => Promise<boolean>;

  /** Opens the approval dialog with the given checkpoint payload. */
  openReview: (config: ICheckpointReviewOpenConfig) => void;
  /** Closes the approval dialog and resets state. */
  closeReview: () => void;
}

/** Configuration for opening the checkpoint approval dialog. */
interface ICheckpointReviewOpenConfig {
  message?: string;
  facts: ICheckpointFact[];
  onDecide: (
    decision: TCheckpointDecision,
    values?: Record<string, string>,
  ) => Promise<boolean>;
}

const initialState: Omit<ICheckpointReviewState, 'openReview' | 'closeReview'> =
  {
    isOpen: false,
    message: undefined,
    facts: [],
    onDecide: undefined,
  };

export const useCheckpointReviewStore = createStore<ICheckpointReviewState>(
  'rain-checkpoint-review-store',
  (_, _get, api) => ({
    ...initialState,

    openReview: (config: ICheckpointReviewOpenConfig) => {
      api.setState(
        {
          isOpen: true,
          message: config.message,
          facts: config.facts,
          onDecide: config.onDecide,
        },
        false,
        { type: 'checkpointReview/openReview', config },
      );
    },

    closeReview: () => {
      api.setState({ ...initialState }, false, 'checkpointReview/closeReview');
    },
  }),
);

/** Whether the checkpoint approval dialog is open. */
export const selectCheckpointReviewIsOpen = (state: ICheckpointReviewState) =>
  state.isOpen;
/** The explanatory message for the current checkpoint. */
export const selectCheckpointReviewMessage = (state: ICheckpointReviewState) =>
  state.message;
/** The runtime facts under review. */
export const selectCheckpointReviewFacts = (state: ICheckpointReviewState) =>
  state.facts;
/** The decision resolver for the current checkpoint. */
export const selectCheckpointReviewOnDecide = (state: ICheckpointReviewState) =>
  state.onDecide;
/** Open-dialog action. */
export const selectCheckpointReviewOpen = (state: ICheckpointReviewState) =>
  state.openReview;
/** Close-dialog action. */
export const selectCheckpointReviewClose = (state: ICheckpointReviewState) =>
  state.closeReview;

export type { ICheckpointReviewState, ICheckpointReviewOpenConfig };
